import {
  FileText,
  CalendarDays,
  FolderOpen,
  CheckCircle2,
  Users,
  Clock3,
  ShieldCheck,
  Wallet,
  FileCheck2,
  CalendarCheck,
  BellRing,
  ScanText,
  Send,
  UserCheck,
} from "lucide-react";

export const WORKFLOWS = [
  {
    id: "overdue-invoice",
    num: "01",
    name: "Overdue Invoice",
    icon: FileText,
    subtitle: "Nova follows up, gets you paid, and keeps records updated.",
    stages: [
      {
        title: "Invoice overdue",
        desc: "Payment is past due based on terms.",
        visual: { kind: "invoice", number: "INV-1042", badge: "OVERDUE", amount: "$1,240", due: "Due Apr 24" },
      },
      {
        title: "Checks details",
        desc: "Reviews payment status, customer history and invoice terms.",
        visual: {
          kind: "kv",
          rows: [
            { label: "Payment status", value: "Unpaid", tone: "red" },
            { label: "Customer", value: "Acme Co." },
            { label: "Terms", value: "Net 30" },
            { label: "Last reminder", value: "None" },
          ],
        },
      },
      {
        title: "Sends reminder",
        desc: "Sends the right reminder and schedules the next follow-up.",
        visual: { kind: "email", title: "Payment reminder", body: "Hi Acme Co., Just a friendly reminder regarding invoice #1042." },
      },
      {
        title: "All set",
        desc: "Keeps records updated and monitors until payment is received.",
        visual: {
          kind: "list",
          header: { title: "Done", pill: { text: "Done", tone: "green" } },
          rows: [
            { icon: "check-green", title: "Reminder sent" },
            { icon: "check-green", title: "Record updated" },
            { icon: "check-green", title: "Next check — May 8" },
          ],
        },
      },
    ],
    metrics: [
      { value: "80%", label: "Faster Follow-ups", icon: Clock3 },
      { value: "98%", label: "Payment Recovery", icon: ShieldCheck },
      { value: "30+", label: "Hours Saved / Month", icon: Wallet },
      { value: "100%", label: "Audit Ready", icon: FileCheck2 },
    ],
  },
  {
    id: "schedule-change",
    num: "02",
    name: "Schedule Change",
    icon: CalendarDays,
    subtitle: "When plans change, Nova adjusts everything and keeps customers happy.",
    stages: [
      {
        title: "Change detected",
        desc: "A disruption is detected in your schedule.",
        visual: {
          kind: "list",
          header: { title: "Today" },
          rows: [
            { icon: "alert", title: "Sarah called out sick", tone: "red" },
            { icon: "dot", title: "Consultation", sub: "9:00 AM" },
            { icon: "dot", title: "On-site visit", sub: "10:30 AM" },
            { icon: "dot", title: "Team training", sub: "1:00 PM" },
          ],
        },
      },
      {
        title: "Nova checks",
        desc: "Reviews availability, appointments and customer constraints.",
        visual: {
          kind: "list",
          rows: [
            { icon: "check-blue", title: "Staff availability" },
            { icon: "check-blue", title: "Appointments" },
            { icon: "check-blue", title: "Customer needs" },
            { icon: "check-blue", title: "Buffer time" },
          ],
        },
      },
      {
        title: "Nova reworks",
        desc: "Reassigns, reschedules and finds the best alternative times.",
        visual: {
          kind: "list",
          rows: [
            { icon: "dot", title: "Consultation", sub: "9 AM", chip: { text: "Reassigned", tone: "blue" } },
            { icon: "dot", title: "On-site visit", sub: "10 AM", chip: { text: "Kept", tone: "green" } },
            { icon: "dot", title: "Team training", sub: "11 AM / 1 PM", chip: { text: "Moved", tone: "slate" } },
          ],
        },
      },
      {
        title: "Update sent",
        desc: "Nova notifies your team and customers automatically.",
        visual: {
          kind: "list",
          header: { title: "Schedule updated", pill: { text: "Sent", tone: "blue" } },
          rows: [
            { icon: "check-green", title: "4 appointments preserved" },
            { icon: "check-green", title: "Customers notified" },
            { icon: "check-green", title: "Calendar updated" },
          ],
        },
      },
      {
        title: "All set",
        desc: "Everyone is informed and your day stays on track.",
        visual: {
          kind: "list",
          header: { title: "Schedule recovered", pill: { text: "Done", tone: "green" } },
          rows: [
            { icon: "check-green", title: "4 appointments preserved" },
            { icon: "check-green", title: "Customers notified" },
            { icon: "check-green", title: "Calendar updated" },
          ],
        },
      },
    ],
    metrics: [
      { value: "80%", label: "Faster Rescheduling", icon: Clock3 },
      { value: "95%", label: "Appointments Saved", icon: CalendarCheck },
      { value: "100%", label: "Customers Notified", icon: BellRing },
      { value: "90%", label: "Less Schedule Conflict", icon: ShieldCheck },
    ],
  },
  {
    id: "document-intake",
    num: "03",
    name: "Document Intake",
    icon: FolderOpen,
    subtitle: "Nova captures, understands and routes documents to the right place.",
    stages: [
      {
        title: "Document received",
        desc: "From email, upload or integration.",
        visual: { kind: "doc", name: "Contract.pdf", from: "From: Sarah@acme.com" },
      },
      {
        title: "Nova understands",
        desc: "Extracts key information and classifies the document automatically.",
        visual: {
          kind: "kv",
          rows: [
            { label: "Type", value: "Contract" },
            { label: "From", value: "Acme Co." },
            { label: "Date", value: "Apr 28, 2025" },
            { label: "Value", value: "$24,000" },
            { label: "Renewal", value: "Apr 28, 2026" },
            { label: "Owner", value: "Legal Team" },
          ],
        },
      },
      {
        title: "Routed & stored",
        desc: "Saves in the right place and links to the right people.",
        visual: {
          kind: "folder",
          rows: [
            { title: "Contracts", depth: 0 },
            { title: "Acme Co.", depth: 1 },
            { title: "2025", depth: 2 },
            { title: "Contract_Acme.pdf", depth: 3, file: true },
          ],
        },
      },
      {
        title: "Systems updated",
        desc: "Updates records and notifies your systems and team.",
        visual: {
          kind: "list",
          rows: [
            { icon: "check-green", title: "Record created" },
            { icon: "check-green", title: "CRM updated" },
            { icon: "check-green", title: "Drive linked" },
            { icon: "check-green", title: "Team notified" },
          ],
        },
      },
      {
        title: "All set",
        desc: "Everything is in place and ready to go.",
        visual: { kind: "big", title: "Document processed" },
      },
    ],
    metrics: [
      { value: "80%", label: "Faster Processing", icon: Clock3 },
      { value: "98%", label: "Accurate Extraction", icon: ScanText },
      { value: "30+", label: "Hours Saved / Month", icon: Wallet },
      { value: "100%", label: "Audit Ready", icon: FileCheck2 },
    ],
  },
  {
    id: "approval-follow-up",
    num: "04",
    name: "Approval & Follow-up",
    icon: CheckCircle2,
    subtitle: "Nova gets the right people to approve, chases what's pending, and keeps things moving.",
    stages: [
      {
        title: "Request created",
        desc: "Nova creates the request with all the right details and context.",
        visual: {
          kind: "list",
          header: { title: "Marketing budget approval", icon: "file" },
          rows: [{ icon: "user", title: "Requested by Sarah@acme.com" }],
        },
      },
      {
        title: "Sent for approval",
        desc: "Sends to the right approvers in the right order automatically.",
        visual: {
          kind: "list",
          rows: [
            { icon: "user", title: "Jane Smith", sub: "Manager" },
            { icon: "user", title: "Mike Johnson", sub: "Finance" },
            { icon: "user", title: "Priya Shah", sub: "Director" },
          ],
          footer: { text: "Due Apr 30", tone: "slate" },
        },
      },
      {
        title: "Follow-ups sent",
        desc: "Nova checks progress and sends timely reminders.",
        visual: {
          kind: "list",
          rows: [
            { icon: "dot", title: "Reminder to Jane", sub: "Apr 24, 9:00 AM" },
            { icon: "dot", title: "Reminder to Mike", sub: "Apr 25, 1:30 PM" },
            { icon: "dot", title: "Reminder to Priya", sub: "Apr 26, 10:00 AM" },
          ],
        },
      },
      {
        title: "Approved",
        desc: "Approvers respond and Nova tracks every update.",
        visual: {
          kind: "list",
          rows: [
            { icon: "check-green", title: "Jane Smith", sub: "Approved Apr 26" },
            { icon: "check-green", title: "Mike Johnson", sub: "Approved Apr 27" },
            { icon: "check-green", title: "Priya Shah", sub: "Approved Apr 28" },
          ],
        },
      },
      {
        title: "Completed",
        desc: "Everything is updated across systems and your team.",
        visual: {
          kind: "list",
          header: { title: "All approvals completed", pill: { text: "Done", tone: "green" } },
          rows: [{ icon: "check-green", title: "Budget approved and recorded" }],
        },
      },
    ],
    metrics: [
      { value: "70%", label: "Faster Approvals", icon: Clock3 },
      { value: "90%", label: "Follow-ups Automated", icon: Send },
      { value: "25+", label: "Hours Saved / Month", icon: Wallet },
      { value: "100%", label: "Audit Trail Ready", icon: FileCheck2 },
    ],
  },
  {
    id: "customer-onboarding",
    num: "05",
    name: "Customer Onboarding",
    icon: Users,
    subtitle: "Nova streamlines onboarding, gets everything set up, and keeps customers informed.",
    stages: [
      {
        title: "New customer signed up",
        desc: "Captured automatically from your website or integrations.",
        visual: {
          kind: "list",
          header: { title: "Acme Co.", pill: { text: "Plan: Professional", tone: "blue" } },
          rows: [{ icon: "user", title: "acme@acme.com", stacked: true }],
        },
      },
      {
        title: "Information collected",
        desc: "Nova gathers all key details and requirements in one place.",
        visual: {
          kind: "list",
          rows: [
            { icon: "check-blue", title: "Company details" },
            { icon: "check-blue", title: "Billing info" },
            { icon: "check-blue", title: "Team members" },
            { icon: "check-blue", title: "Requirements" },
          ],
          footer: { text: "All completed.", tone: "green" },
        },
      },
      {
        title: "Accounts & access setup",
        desc: "Creates accounts, connects tools, and sets permissions automatically.",
        visual: {
          kind: "list",
          rows: [
            { icon: "check-blue", title: "Workspace" },
            { icon: "check-blue", title: "Integrations" },
            { icon: "check-blue", title: "Permissions" },
            { icon: "check-blue", title: "Roles assigned" },
          ],
          footer: { text: "All completed.", tone: "green" },
        },
      },
      {
        title: "Welcome & resources sent",
        desc: "Sends a personalized welcome email and helpful resources.",
        visual: {
          kind: "list",
          header: { title: "Welcome sent", icon: "send" },
          rows: [
            { icon: "check-green", title: "Welcome email sent" },
            { icon: "check-green", title: "Onboarding guide shared" },
          ],
        },
      },
      {
        title: "Onboarding complete",
        desc: "Everything is ready and your customer can start using your product.",
        visual: { kind: "big", title: "Customer is all set and ready to go!" },
      },
    ],
    metrics: [
      { value: "75%", label: "Faster Onboarding", icon: Clock3 },
      { value: "95%", label: "Setup Accuracy", icon: ShieldCheck },
      { value: "20+", label: "Hours Saved / Onboard", icon: Wallet },
      { value: "100%", label: "Customer Ready", icon: UserCheck },
    ],
  },
];
